import { useMemo, useState } from 'react'
import EditableField from '../components/EditableField'
import useDealStore from '../store/dealStore'
import useAuthStore from '../store/authStore'
import '../styles/module7.css'
import '../styles/editable-field.css'

const money = (value) => `$${Math.round(value || 0).toLocaleString()}`
const pct = (value) => `${((value || 0) * 100).toFixed(2)}%`

function buildPackage(dealState, tone, authUser) {
  const raw = dealState.rawDealData || {}
  const financials = dealState.extractedFinancials || {}
  const underwriting = dealState.underwritingMetrics || {}
  const score = dealState.scoreAndOffer || {}
  const lenderMatches = dealState.lenderMatches || {}
  const topLoan = lenderMatches.topRecommendation || {}
  const price = Number(raw.price) || 0
  const units = Number(raw.property?.units) || financials.revenue?.rentRoll?.totalUnits || 0
  const noi = Number(underwriting.noi || financials.noi) || 0
  const revenue = Number(financials.revenue?.totalRevenue) || 0
  const expenses = Number(financials.expenses?.totalExpenses || financials.expenses?.totalOperatingExpenses) || 0
  const loanAmount = Number(topLoan.loanAmount || underwriting.debtService?.loanAmount) || price * 0.7
  const equity = Math.max(0, price - loanAmount)
  const ownershipCosts = dealState.ownershipCosts || {}
  const propertyName = dealState.dealName || 'Selected Multifamily Asset'
  const location = [raw.location?.address, raw.location?.city, raw.location?.state].filter(Boolean).join(', ')
  const toneLine = tone === 'conservative'
    ? 'The package emphasizes durable cash flow, debt coverage, and lender downside protection.'
    : tone === 'growth'
      ? 'The package emphasizes upside, execution speed, and value-add potential.'
      : 'The package balances cash-flow stability, lender risk controls, and upside potential.'

  const sections = [
    {
      id: 'executiveSummary',
      title: 'Executive Summary',
      status: 'ready',
      body: `${propertyName} is a ${units || 'multi'}-unit ${raw.property?.buildingType || 'multifamily'} acquisition located at ${location || 'the selected market'}. The request is structured around ${topLoan.loanProduct || 'the recommended loan product'} from ${topLoan.lenderName || 'the top matched lender'}, with requested proceeds of ${money(loanAmount)}. ${toneLine}`,
    },
    {
      id: 'propertyOverview',
      title: 'Property Overview',
      status: 'ready',
      body: `${propertyName} includes ${units || 'TBD'} units and was built in ${raw.property?.yearBuilt || 'TBD'}. The property profile is organized for lender review with address, asset type, unit count, acquisition price, and operating context.`,
    },
    {
      id: 'financialSummary',
      title: 'Financial Summary (T12)',
      status: revenue && noi ? 'ready' : 'review',
      body: (() => {
        const ownerCosts = dealState.ownershipCosts || {}
        const insuranceLine = ownerCosts.annualInsurance
          ? ` Annual insurance: ${money(ownerCosts.annualInsurance)}${ownerCosts.insuranceIsEstimated ? ' (estimated)' : ''}.`
          : ''
        const beo = underwriting.breakEvenOccupancy != null
          ? ` Break-even occupancy: ${(underwriting.breakEvenOccupancy * 100).toFixed(1)}%.`
          : ''
        const monthlyLine = ownerCosts.totalMonthlyOwnershipCost
          ? ` Total monthly ownership cost (debt + all operating expenses): ${money(ownerCosts.totalMonthlyOwnershipCost)}/mo.`
          : ''
        return `T12 summary shows total revenue of ${money(revenue)}, operating expenses of ${money(expenses)}, and NOI of ${money(noi)}. Entry cap rate is ${pct(underwriting.capRate?.entry)} with DSCR of ${(underwriting.dscr?.current || 0).toFixed(2)}x.${insuranceLine}${beo}${monthlyLine}`
      })(),
    },
    {
      id: 'rentRollSummary',
      title: 'Rent Roll Summary',
      status: 'ready',
      body: `Rent roll summary includes ${financials.revenue?.rentRoll?.occupiedUnits || Math.round(units * 0.91) || 'TBD'} occupied units, ${financials.revenue?.rentRoll?.occupancyRate || 91}% occupancy, and average rent of ${money(financials.revenue?.rentRoll?.averageRent || (revenue / 12 / Math.max(units, 1)))} per unit.`,
    },
    {
      id: 'sourcesUses',
      title: 'Sources & Uses',
      status: 'ready',
      body: `Sources include proposed loan proceeds of ${money(loanAmount)} and estimated sponsor equity of ${money(equity)}. Uses include purchase price of ${money(price)}, closing costs, reserves, and initial CapEx allocation.`,
    },
    {
      id: 'capexPlan',
      title: 'CapEx Plan',
      status: 'review',
      body: `Initial CapEx narrative is prepared for lender review. The package highlights deferred maintenance diligence, unit interior improvements, exterior/common-area upgrades, and reserve planning where applicable.`,
    },
    {
      id: 'sponsorBio',
      title: 'Borrower / Sponsor Bio',
      status: authUser?.name ? 'ready' : 'review',
      body: authUser?.name
        ? `Borrower: ${authUser.name}${authUser.email ? ` (${authUser.email})` : ''}. This package has been prepared by the sponsor for lender review. The borrower profile includes acquisition experience, liquidity position, net worth statement, portfolio history, and execution track record. Full sponsor documentation is available upon request.`
        : `Borrower name and contact not yet provided. Complete your profile or enter sponsor details before final submission. This section should include borrower experience, liquidity, net worth, portfolio history, and execution track record.`,
    },
    {
      id: 'marketOverview',
      title: 'Market Overview',
      status: 'ready',
      body: `${raw.location?.city || 'The target market'} is summarized with demand drivers, rent growth context, occupancy assumptions, and lender-relevant market risk notes from the market intelligence workflow.`,
    },
    {
      id: 'exitStrategy',
      title: 'Exit Strategy',
      status: 'ready',
      body: `Exit strategy is based on a ${underwriting.irr?.holdingPeriod || 5}-year hold, stabilization of cash flow, and sale or refinance after operational improvements. The package frames lender repayment through durable NOI and refinance/sale optionality.`,
    },
    {
      id: 'supportingDocs',
      title: 'Supporting Documents List',
      status: 'ready',
      body: 'Package checklist includes Offering Memorandum, Rent Roll, T12/P&L, property photos, lender match summary, underwriting metrics, deal score output, sponsor bio, and diligence items.',
    },
  ]

  const completeness = Math.round((sections.filter((section) => section.status === 'ready').length / sections.length) * 100)
  const missingItems = sections.filter((section) => section.status !== 'ready').map((section) => section.title)
  const submissionReadiness = [
    { label: 'Core narrative', ready: sections.slice(0, 5).every(section => section.status === 'ready') },
    { label: 'Lender terms mapped', ready: !!topLoan.lenderName && !!loanAmount },
    { label: 'Financials attached', ready: !!noi && !!revenue },
    { label: 'Review sections flagged', ready: missingItems.length <= 2 },
  ]

  return {
    sections,
    completeness,
    missingItems,
    submissionReadiness,
    summary: {
      propertyName,
      location,
      price,
      units,
      loanAmount,
      equity,
      lenderName: topLoan.lenderName || 'Top Matched Lender',
      loanProduct: topLoan.loanProduct || 'Recommended Loan',
      rate: topLoan.rate || underwriting.debtService?.interestRate || 0,
      ltv: topLoan.ltv || underwriting.debtService?.ltv || 0,
      closingTimeline: topLoan.closingTimeline || '30-45 days',
      score: score.numericalScore || 0,
    },
  }
}

function applySummaryOverrides(loanPackage, overrides) {
  const summary = {
    ...loanPackage.summary,
    loanAmount: overrides.loanAmount ?? loanPackage.summary.loanAmount,
    ltv: overrides.ltv ?? loanPackage.summary.ltv,
    rate: overrides.rate ?? loanPackage.summary.rate,
    closingTimeline: overrides.closingTimeline || loanPackage.summary.closingTimeline,
  }
  const equity = Math.max(0, summary.price - summary.loanAmount)
  const sections = loanPackage.sections.map(section => {
    if (section.id === 'executiveSummary') {
      return {
        ...section,
        body: section.body.replace(/requested proceeds of \$[\d,]+/, `requested proceeds of ${money(summary.loanAmount)}`),
      }
    }
    if (section.id === 'sourcesUses') {
      return {
        ...section,
        body: section.body
          .replace(/proposed loan proceeds of \$[\d,]+/, `proposed loan proceeds of ${money(summary.loanAmount)}`)
          .replace(/estimated sponsor equity of \$[\d,]+/, `estimated sponsor equity of ${money(equity)}`),
      }
    }
    return section
  })

  return {
    ...loanPackage,
    sections,
    summary: {
      ...summary,
      equity,
    },
  }
}

function SectionRow({ section, active, onClick }) {
  return (
    <button className={`m7-section-row ${active ? 'active' : ''}`} onClick={onClick}>
      <span className={`m7-status-dot ${section.status}`} />
      <span>{section.title}</span>
      <strong>{section.status === 'ready' ? 'Ready' : 'Review'}</strong>
    </button>
  )
}

function Module7Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const { user: authUser } = useAuthStore()
  const tone = 'balanced'
  const [selectedSection, setSelectedSection] = useState('executiveSummary')
  const [generated, setGenerated] = useState(false)
  const [copyDone, setCopyDone] = useState(false)
  const [savedVersions, setSavedVersions] = useState([])  // Feature 1: version history
  // Fix 9: editable email compose state
  const [showEmailCompose, setShowEmailCompose] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [summaryOverrides, setSummaryOverrides] = useState({})

  const baseLoanPackage = useMemo(() => buildPackage(dealState, tone, authUser), [dealState, tone, authUser])
  const loanPackage = useMemo(
    () => applySummaryOverrides(baseLoanPackage, summaryOverrides),
    [baseLoanPackage, summaryOverrides]
  )
  const activeSection = loanPackage.sections.find((section) => section.id === selectedSection) || loanPackage.sections[0]

  const updateSummaryOverride = (field, value) => {
    setSummaryOverrides(prev => ({ ...prev, [field]: value }))
  }

  const packageText = useMemo(() => {
    const lines = [
      'AcquireOS AI Loan Request Package',
      loanPackage.summary.propertyName,
      loanPackage.summary.location,
      '',
      `Target Lender: ${loanPackage.summary.lenderName}`,
      `Loan Product: ${loanPackage.summary.loanProduct}`,
      `Loan Amount: ${money(loanPackage.summary.loanAmount)}`,
      `Rate: ${pct(loanPackage.summary.rate)}`,
      `LTV: ${pct(loanPackage.summary.ltv)}`,
      '',
      ...loanPackage.sections.flatMap((section) => [
        section.title,
        section.body,
        '',
      ]),
    ]
    return lines.join('\n')
  }, [loanPackage])

  const handleDownload = () => {
    const dealName = loanPackage.summary.propertyName || 'Deal'
    const safeName = dealName.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'LoanPackage'
    const dateStr  = new Date().toISOString().split('T')[0].replace(/-/g, '')
    // Use .doc (Word 97-2003 HTML format) — Word opens HTML blobs cleanly with this extension.
    // .docx requires a ZIP/OOXML archive; using .docx with HTML content triggers the Word warning.
    const filename = `LoanPackage_${safeName}_${dateStr}.doc`

    const sectionsHtml = loanPackage.sections.map(s => `
      <h2 style="color:#1B3A6B;font-size:14pt;border-bottom:1px solid #1B3A6B;padding-bottom:4pt;margin-top:18pt;page-break-after:avoid">${s.title}</h2>
      <p style="font-size:11pt;line-height:1.6;text-align:justify;margin:6pt 0 12pt">${s.body}</p>
    `).join('')

    // UTF-8 BOM prevents Word from mis-reading the encoding
    const BOM = '﻿'
    const html = `${BOM}<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8"/>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8"/>
<!--[if gte mso 9]>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
  </w:WordDocument>
</xml>
<![endif]-->
<title>Loan Package — ${dealName}</title>
<style>
  /* Page setup */
  @page { size: 8.5in 11in; margin: 1in 1in 1in 1in; }
  body  { font-family: "Calibri", "Arial", sans-serif; font-size: 11pt; color: #1f2933; line-height: 1.5; margin: 0; padding: 0; }

  /* Header banner */
  .doc-header   { background: #1B3A6B; color: #fff; padding: 18pt 28pt; margin-bottom: 0; }
  .doc-header h1 { margin: 0 0 4pt; font-size: 20pt; font-weight: 700; }
  .doc-header p  { margin: 0; font-size: 10pt; opacity: 0.85; }

  /* Body */
  .doc-body  { padding: 20pt 32pt; }
  h1.doc-title { color: #1B3A6B; font-size: 16pt; margin: 0 0 14pt; }

  /* Summary table */
  table  { width: 100%; border-collapse: collapse; margin: 12pt 0 18pt; font-size: 10pt; }
  th, td { border: 1pt solid #c8d8e8; padding: 6pt 8pt; vertical-align: middle; }
  th     { background: #dbeafe; color: #1e3a8a; font-weight: 700; text-align: left; width: 18%; }
  td     { background: #fff; }
  tr:nth-child(even) td { background: #f8fafc; }

  /* Section headings */
  h2 { color: #1B3A6B; font-size: 13pt; border-bottom: 1.5pt solid #1B3A6B;
       padding-bottom: 3pt; margin-top: 20pt; margin-bottom: 6pt; }
  p  { font-size: 11pt; line-height: 1.6; margin: 0 0 10pt; text-align: justify; }

  /* Footer */
  .doc-footer { border-top: 1pt solid #c8d8e8; margin-top: 28pt; padding-top: 8pt;
                font-size: 9pt; color: #6b7280; }
</style>
</head>
<body>
  <div class="doc-header">
    <h1>Loan Request Package</h1>
    <p>${dealName}&nbsp;&middot;&nbsp;${loanPackage.summary.location || ''}&nbsp;&middot;&nbsp;${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </div>
  <div class="doc-body">
    <h1 class="doc-title">${dealName}</h1>
    <table>
      <tr>
        <th>Target Lender</th><td>${loanPackage.summary.lenderName || '—'}</td>
        <th>Loan Amount</th><td><strong>$${Math.round(loanPackage.summary.loanAmount).toLocaleString()}</strong></td>
      </tr>
      <tr>
        <th>Loan Product</th><td>${loanPackage.summary.loanProduct || '—'}</td>
        <th>LTV</th><td>${(loanPackage.summary.ltv * 100).toFixed(2)}%</td>
      </tr>
      <tr>
        <th>Interest Rate</th><td>${(loanPackage.summary.rate * 100).toFixed(2)}%</td>
        <th>Close Timeline</th><td>${loanPackage.summary.closingTimeline || '—'}</td>
      </tr>
      <tr>
        <th>Deal Score</th><td><strong>${loanPackage.summary.score}/100</strong></td>
        <th>Prepared</th><td>${new Date().toLocaleDateString()}</td>
      </tr>
    </table>
    ${sectionsHtml}
    <div class="doc-footer">
      Prepared by AcquireOS AI &nbsp;&middot;&nbsp; Confidential &nbsp;&middot;&nbsp; For lender use only
    </div>
  </div>
</body>
</html>`

    // 'application/msword' + .doc extension = Word opens HTML cleanly, no unreadable-content warning
    const blob = new Blob([html], { type: 'application/msword;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href     = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    setGenerated(true)

    // ── Feature 1: save version snapshot ──
    setSavedVersions(prev => [{
      id: Date.now(),
      ts: new Date().toLocaleString(),
      tone,
      completeness: loanPackage.completeness,
      propertyName: loanPackage.summary.propertyName,
      lenderName: loanPackage.summary.lenderName,
      loanAmount: loanPackage.summary.loanAmount,
    }, ...prev].slice(0, 10))   // keep last 10
  }

  // ── Feature 2: One-Click Submission helpers ──────────────────────────────
  const handleCopy = () => {
    navigator.clipboard.writeText(packageText).then(() => {
      setCopyDone(true)
      setTimeout(() => setCopyDone(false), 2500)
    })
  }

  const handleEmailDraft = () => {
    // Fix 9: open editable compose panel instead of launching mailto directly
    const subject = `Loan Request: ${loanPackage.summary.propertyName} — ${money(loanPackage.summary.loanAmount)}`
    const body =
      `Dear ${loanPackage.summary.lenderName} Team,\n\n` +
      `Please find our loan request for ${loanPackage.summary.propertyName} located at ${loanPackage.summary.location}.\n\n` +
      `Requested Loan Amount: ${money(loanPackage.summary.loanAmount)}\n` +
      `Loan Product: ${loanPackage.summary.loanProduct}\n` +
      `LTV: ${pct(loanPackage.summary.ltv)}\n` +
      `Proposed Rate: ${pct(loanPackage.summary.rate)}\n` +
      `Target Close: ${loanPackage.summary.closingTimeline}\n\n` +
      `Deal Score: ${loanPackage.summary.score}/100\n\n` +
      `Executive Summary:\n${loanPackage.sections[0]?.body}\n\n` +
      `We look forward to discussing further.\n\nBest regards,\nAcquireOS AI Deal Team`
    setEmailSubject(subject)
    setEmailBody(body)
    setShowEmailCompose(true)
  }

  const handleOpenInMail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`
  }

  const handleComplete = () => {
    const documentId = `loan_pkg_${Date.now()}`
    updateDealState({
      loanPackage: {
        documentId,
        executiveSummary: loanPackage.sections.find((section) => section.id === 'executiveSummary')?.body,
        propertyOverview: loanPackage.sections.find((section) => section.id === 'propertyOverview')?.body,
        financialHighlights: loanPackage.sections.find((section) => section.id === 'financialSummary')?.body,
        rentRollSummary: loanPackage.sections.find((section) => section.id === 'rentRollSummary')?.body,
        underwritingSummary: `Deal score ${loanPackage.summary.score}/100, requested loan ${money(loanPackage.summary.loanAmount)} at ${pct(loanPackage.summary.ltv)} LTV, DSCR ${(dealState.underwritingMetrics?.dscr?.current || 0).toFixed(2)}x.`,
        generatedAt: new Date().toISOString(),
        format: 'pdf',
        lenderCustomization: {
          lenderName: loanPackage.summary.lenderName,
          loanProduct: loanPackage.summary.loanProduct,
          narrativeTone: tone,
        },
        packageSections: loanPackage.sections,
        completeness: loanPackage.completeness,
        supportingDocuments: [
          'Offering Memorandum',
          'Rent Roll',
          'T12 / P&L',
          'Property Photos',
          'Underwriting Summary',
          'Deal Score & Verdict',
          'Lender Match Summary',
          'Sponsor Bio',
        ],
      },
    })

    updateAvailableModules()
    setTimeout(() => forceNavigateToModule(9), 500)
  }

  return (
    <div className="m7-page">
      <div className="m7-header">
        <div className="m7-badge">Module 7</div>
        <div>
          <h1>Loan Package Generator</h1>
          <p>AI-generated lender-ready package assembled from deal intake, underwriting, scoring, and lender matches.</p>
        </div>
        <div className="m7-header-metric">
          <span>Completeness</span>
          <strong>{loanPackage.completeness}%</strong>
        </div>
      </div>

      <div className="m7-grid">
        <section className="m7-package-card">
          <div className="m7-title-row">
            <span>One-Click Loan Package</span>
            <strong>{loanPackage.summary.loanProduct}</strong>
          </div>
          <div className="m7-cover">
            <div className="m7-cover-band" />
            <div className="m7-cover-body">
              <span>AcquireOS AI Loan Request Package</span>
              <h2>{loanPackage.summary.propertyName}</h2>
              <p>{loanPackage.summary.location || 'Location pending'}</p>
              <div className="m7-cover-grid">
                <div>
                  <span>Request</span>
                  <strong>
                    <EditableField
                      value={loanPackage.summary.loanAmount}
                      type="number"
                      onSave={value => updateSummaryOverride('loanAmount', value)}
                      displayFormat={money}
                    />
                  </strong>
                </div>
                <div>
                  <span>LTV</span>
                  <strong>
                    <EditableField
                      value={loanPackage.summary.ltv * 100}
                      type="number"
                      onSave={value => updateSummaryOverride('ltv', Math.max(0, value) / 100)}
                      displayFormat={value => `${Number(value || 0).toFixed(2)}%`}
                    />
                  </strong>
                </div>
                <div>
                  <span>Rate</span>
                  <strong>
                    <EditableField
                      value={loanPackage.summary.rate * 100}
                      type="number"
                      onSave={value => updateSummaryOverride('rate', Math.max(0, value) / 100)}
                      displayFormat={value => `${Number(value || 0).toFixed(2)}%`}
                    />
                  </strong>
                </div>
                <div>
                  <span>Close</span>
                  <strong>
                    <EditableField
                      value={loanPackage.summary.closingTimeline}
                      onSave={value => updateSummaryOverride('closingTimeline', value)}
                      placeholder="Enter close timeline"
                    />
                  </strong>
                </div>
              </div>
            </div>
          </div>
          <div className="m7-action-row">
            <button className="m7-primary-btn" onClick={handleDownload}>
              Generate Loan Package (PDF)
            </button>
          </div>
          {generated && <div className="m7-generated-note">Package generated and ready for lender submission.</div>}

          {/* ── Feature 2: One-Click Submission ── */}
          <div className="m7-submit-row">
            <div className="m7-submit-label">One-Click Submission</div>
            <div className="m7-submit-btns">
              <button
                className={`m7-submit-btn m7-copy-btn ${copyDone ? 'done' : ''}`}
                onClick={handleCopy}
                disabled={copyDone}
              >
                {copyDone ? '✓ Copied!' : '📋 Copy Package'}
              </button>
              <button className="m7-submit-btn m7-email-btn" onClick={handleEmailDraft}>
                📧 Draft Email
              </button>
            </div>
            <p className="m7-submit-hint">Copy the full package text to your clipboard, or open a pre-filled lender email draft.</p>
          </div>

          {/* ── Fix 9: Editable Email Compose Panel ── */}
          {showEmailCompose && (
            <div className="m7-email-compose">
              <div className="m7-email-compose-header">
                <span>✉️ Edit Email Before Sending</span>
                <button className="m7-compose-close" onClick={() => setShowEmailCompose(false)}>✕</button>
              </div>
              <div className="m7-email-field">
                <label>Subject</label>
                <input
                  className="m7-email-input"
                  type="text"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                />
              </div>
              <div className="m7-email-field">
                <label>Body</label>
                <textarea
                  className="m7-email-textarea"
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  rows={14}
                />
              </div>
              <div className="m7-email-compose-actions">
                <button className="m7-submit-btn" onClick={() => setShowEmailCompose(false)}>Cancel</button>
                <button className="m7-submit-btn m7-email-btn" onClick={handleOpenInMail}>
                  📬 Open in Mail →
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="m7-lender-card">
          <div className="m7-title-row">
            <span>Lender-Specific Customization</span>
            <strong>{loanPackage.summary.lenderName}</strong>
          </div>
          <div className="m7-lender-details">
            <div><span>Selected Lender</span><strong>{loanPackage.summary.lenderName}</strong></div>
            <div><span>Loan Type</span><strong>{loanPackage.summary.loanProduct}</strong></div>
            <div><span>Deal Score</span><strong>{loanPackage.summary.score}/100</strong></div>
            <div><span>Equity</span><strong>{money(loanPackage.summary.equity)}</strong></div>
            {dealState.ownershipCosts?.annualInsurance && (
              <div>
                <span>
                  Annual Insurance
                  {dealState.ownershipCosts.insuranceIsEstimated && <em style={{fontSize:'10px',color:'#d97706'}}> (est.)</em>}
                </span>
                <strong>{money(dealState.ownershipCosts.annualInsurance)}</strong>
              </div>
            )}
            {dealState.ownershipCosts?.monthlyDebtService && (
              <div>
                <span>Monthly Debt Service</span>
                <strong>{money(dealState.ownershipCosts.monthlyDebtService)}/mo</strong>
              </div>
            )}
            {dealState.ownershipCosts?.totalMonthlyOwnershipCost && (
              <div>
                <span>Total Monthly Cost</span>
                <strong>{money(dealState.ownershipCosts.totalMonthlyOwnershipCost)}/mo</strong>
              </div>
            )}
            {dealState.underwritingMetrics?.breakEvenOccupancy != null && (
              <div>
                <span>Break-Even Occupancy</span>
                <strong>{(dealState.underwritingMetrics.breakEvenOccupancy * 100).toFixed(1)}%</strong>
              </div>
            )}
          </div>
        </section>

        <section className="m7-sections-card">
          <div className="m7-title-row">
            <span>Comprehensive Package Structure</span>
            <strong>{loanPackage.sections.length} sections</strong>
          </div>
          <div className="m7-section-list">
            {loanPackage.sections.map((section) => (
              <SectionRow
                key={section.id}
                section={section}
                active={activeSection.id === section.id}
                onClick={() => setSelectedSection(section.id)}
              />
            ))}
          </div>
        </section>

        <section className="m7-preview-card">
          <div className="m7-title-row">
            <span>Editable Section Preview</span>
            <strong>{activeSection.status === 'ready' ? 'Ready' : 'Review needed'}</strong>
          </div>
          <h3>{activeSection.title}</h3>
          <p>{activeSection.body}</p>
        </section>

        <section className="m7-quality-card">
          <div className="m7-title-row">
            <span>Smart Completeness Checker</span>
            <strong>{loanPackage.missingItems.length ? 'Action items' : 'Complete'}</strong>
          </div>
          <div className="m7-complete-meter">
            <div style={{ width: `${loanPackage.completeness}%` }} />
          </div>
          {loanPackage.missingItems.length ? (
            <div className="m7-missing-list">
              {loanPackage.missingItems.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          ) : (
            <p className="m7-clean-note">All required package sections are ready for lender review.</p>
          )}
        </section>

        <section className="m7-readiness-card">
          <div className="m7-title-row">
            <span>Submission Readiness</span>
            <strong>{loanPackage.submissionReadiness.filter(item => item.ready).length}/4 ready</strong>
          </div>
          <div className="m7-readiness-list">
            {loanPackage.submissionReadiness.map(item => (
              <div key={item.label} className={item.ready ? 'ready' : 'review'}>
                <span>{item.ready ? 'OK' : 'REV'}</span>
                <strong>{item.label}</strong>
              </div>
            ))}
          </div>
        </section>

        {/* ── Feature 1: Version History ── */}
        <section className="m7-versions-card">
          <div className="m7-title-row">
            <span>📂 Package Version History</span>
            <strong>{savedVersions.length} saved</strong>
          </div>
          {savedVersions.length === 0 ? (
            <div className="m7-versions-empty">
              <span>No saved versions yet.</span>
              <p>Generate a package to create your first version snapshot.</p>
            </div>
          ) : (
            <div className="m7-versions-list">
              {savedVersions.map((v, i) => (
                <div key={v.id} className="m7-version-row">
                  <div className="m7-version-badge">v{savedVersions.length - i}</div>
                  <div className="m7-version-info">
                    <strong>{v.propertyName}</strong>
                    <span>{v.ts} · {v.tone} tone · {v.completeness}% complete</span>
                    <span>{money(v.loanAmount)} → {v.lenderName}</span>
                  </div>
                  <span className="m7-version-tag">{i === 0 ? 'Latest' : 'Prior'}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <div className="module-bottom-advance">
        <button className="m7-secondary-btn" onClick={handleComplete}>
          Finalize Loan Package &amp; Advance to Module 8
        </button>
      </div>
    </div>
  )
}

export default Module7Page
